import {
  API_BASE_URL,
  parseIstatecResponse,
  parseValidationError,
  type IstatecResponse,
} from './apiConfig';

export type ChatRole = 'user' | 'assistant';

export interface ChatTurn {
  role: ChatRole;
  text: string;
}

export interface StartAiSessionPayload {
  name: string;
  work_email: string;
  /** Composed phone number, e.g. "+91-9876543210". */
  phone_number: string;
}

export interface VerifyAiSessionPayload {
  work_email: string;
  otp: string;
  /**
   * Forwarded from the start-session step so the transcript stored in MongoDB
   * can record who the user was. Optional server-side.
   */
  name?: string;
  /** Forwarded from the start-session step so the lead email is complete. */
  phone_number?: string;
}

export interface AiChatPayload {
  session_token: string;
  work_email: string;
  message: string;
  history?: ChatTurn[];
}

// NOTE: These carry an index signature so they satisfy the
// `Record<string, unknown> | null` constraint on `IstatecResponse<T>`.
export interface VerifyAiSessionData {
  session_token: string;
  work_email: string;
  expires_at: number;
  [key: string]: unknown;
}

export interface AiChatReplyData {
  reply: string;
  model: string;
  [key: string]: unknown;
}

const networkError = (): IstatecResponse => ({
  status: 'error',
  message: 'Network error. Please check your connection and try again.',
  status_code: 0,
  data: null,
  request_id: '',
});

/**
 * Step 1 — POST /ai_chat_start_session
 * Kicks off the email-OTP gate. Only work-email domains are accepted.
 */
export async function startAiChatSession(
  payload: StartAiSessionPayload,
): Promise<IstatecResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}/ai_chat_start_session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 422) return parseValidationError(res);
    return parseIstatecResponse(res);
  } catch {
    return networkError();
  }
}

/**
 * Step 2 — POST /ai_chat_verify_session
 * On success, `data.session_token` is a signed token valid for 24h.
 */
export async function verifyAiChatSession(
  payload: VerifyAiSessionPayload,
): Promise<IstatecResponse<VerifyAiSessionData | null>> {
  try {
    const res = await fetch(`${API_BASE_URL}/ai_chat_verify_session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 422)
      return (await parseValidationError(res)) as IstatecResponse<VerifyAiSessionData | null>;
    return (await parseIstatecResponse(res)) as IstatecResponse<VerifyAiSessionData | null>;
  } catch {
    return networkError() as IstatecResponse<VerifyAiSessionData | null>;
  }
}

/**
 * Step 3 — POST /ai_chat
 * Sends one message; server prepends the Istatec system prompt.
 */
export async function sendAiChatMessage(
  payload: AiChatPayload,
): Promise<IstatecResponse<AiChatReplyData | null>> {
  try {
    const res = await fetch(`${API_BASE_URL}/ai_chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 422)
      return (await parseValidationError(res)) as IstatecResponse<AiChatReplyData | null>;
    return (await parseIstatecResponse(res)) as IstatecResponse<AiChatReplyData | null>;
  } catch {
    return networkError() as IstatecResponse<AiChatReplyData | null>;
  }
}

// ---------------------------------------------------------------------------
// Session persistence (localStorage)
// ---------------------------------------------------------------------------

const SESSION_KEY = 'istatec_ai_chat_session_v1';

export interface StoredAiSession {
  session_token: string;
  work_email: string;
  expires_at: number; // unix seconds
  name?: string;
}

export function loadStoredAiSession(): StoredAiSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAiSession;
    if (!parsed?.session_token || !parsed?.work_email || !parsed?.expires_at) return null;
    if (parsed.expires_at * 1000 <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function persistAiSession(session: StoredAiSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Ignore storage-quota / disabled-storage errors — session just won't persist.
  }
}

export function clearAiSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // no-op
  }
}
